<template>
<div>
    <navbar/>
    <error-panel showInPopup="false" />

    <header class="hero py-6">
        <div class="container">
            <h1 class="display-4">Need Help?</h1>
            <p class="lead text-muted">Learn how to manage your bookmarks using desktop browsers and the iOS app.</p>
        </div>
    </header>

            <!-- Install Bookmarklet -->
            <section class="section bg-white text-center">
                <div class="container">
                    <h4 class="mb-4">How do I add the Bookmarklet to my desktop browser?</h4>
                    <p class="lead mb-4">To be able to quickly and conveniently save webpages, you need to place the 'just bookmark it' button to your bookmarks bar.</p>
                    <p class="lead text-muted" style="font-size: 0.9em">
                        Drag and drop me!👇 <Bookmarklet/>
                    </p>

                    <div class="row justify-content-center mt-5">
                        <div class="col-md-10 text-left alert-info p-3 alert">
                            <h5>Can’t see your bookmarks bar?</h5>
                            <ul class="list-unstyled mt-3">
                                <li><strong>Chrome:</strong> Click the three dots (⋮) → <em>Bookmarks</em> → <em>Show bookmarks bar</em></li>
                                <li><strong>Safari:</strong> Go to the <em>View</em> menu → <em>Show Favorites Bar</em></li>
                                <li><strong>Firefox:</strong> Click the three lines (☰) → <em>Bookmarks</em> → <em>Show Bookmarks Toolbar</em></li>
                                <li><strong>Edge:</strong> Click the three dots (⋯) → <em>Favorites</em> → <em>Show favorites bar</em></li>
                            </ul>
                            <p class="text-muted mt-3">Once the bar is visible, drag the button above into it.</p>
                        </div>
                    </div>

                    <div class="row justify-content-center mt-4">
                        <div class="col-md-8">
                            <video src="@/assets/help/help-bookmarklet-drag.mp4"
                                type="video/mp4"
                                controls = "true"
                                autoplay="autoplay"
                                muted="muted"
                                loop="loop"
                                playsinline="playsinline"
                                preload="auto"
                                class="img-fluid rounded shadow">
                                Your browser does not support the video tag.
                            </video>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Use Bookmarklet -->
            <section class="section bg-light text-center">
                <div class="container">
                    <h4 class="mb-4">How do I use the Bookmarklet to save bookmarks?</h4>
                    <p class="lead">When you're on a page you want to save, click the <strong>“just bookmark it!”</strong> button on your bookmarks bar.</p>
                    <p class="text-muted">You'll be taken to a save screen where you can edit the title, add tags, and confirm.</p>
                    <div class="row justify-content-center mt-4">
                        <div class="col-md-8">
                            <video src="@/assets/help/help-bookmarklet-usage.mp4"
                                   type="video/mp4"
                                   controls = "true"
                                   autoplay="autoplay"
                                   muted="muted"
                                   loop="loop"
                                   playsinline="playsinline"
                                   preload="auto"
                                   class="img-fluid rounded shadow">
                                Your browser does not support the video tag.
                            </video>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Try It Now -->
            <section class="section bg-white text-center">
                <div class="container">
                    <h4 class="mb-4">Can I experiment by saving a sample page?</h4>
                    <p class="lead">Of course, just click on the button below to visit a sample page and click the Bookmarklet to save and tag it:</p>
                    <a href="/example-article.html" class="btn btn-outline-secondary btn-lg mt-5" target="_blank">Open Sample Page</a>
                </div>
            </section>

            <!-- iOS App -->
            <section class="section bg-light text-center">
                <div class="container">
                    <h4 class="mb-4">How do I get the iOS app?</h4>
                    <p class="lead text-muted">Access your saved links anytime, anywhere.</p>

                    <div class="row justify-content-center mt-4">
                        <div class="col-md-6">
                            <img src="@/assets/help/help-ios-app-screenshot.png" alt="iOS App Screenshot" class="img-fluid rounded">
                        </div>
                    </div>

                    <p class="lead text-muted mt-3">Visit the app store to download the app.</p>
                    <a href="https://apps.apple.com/us/app/just-bookmark-it/id6749245868" class="btn btn-primary btn-lg mt-5" target="_blank">
                        Download on the App Store
                    </a>

                </div>
            </section>

            <!-- iOS Sharing Instructions -->
            <section class="section bg-white text-center" id="help-ios">
                <div class="container">
                    <h4 class="mb-4">How do I save bookmarks on iOS?</h4>
                    <p class="lead">You can save links from Safari or any mobile browser using the native Share menu.</p>

                    <div class="row justify-content-center text-left mt-5">
                        <div class="col-md-10 text-left alert-info p-3 alert">
                            <ol class="pl-3">
                                <li><strong>Open a web page</strong> in Safari (or any other mobile browser) on your iPhone or iPad that you want to save.</li>
                                <li><strong>Tap the Share icon</strong> (it looks like a box with an arrow pointing up).</li>
                                <li><strong>Select “just bookmark it!”</strong> from the list of apps in the Share Sheet. You may need to scroll or tap “More” to enable it the first time.</li>
                                <li><strong>The save screen will appear</strong> inside the app — the same one you see on desktop! You can edit the title, add tags, and hit Save.</li>
                            </ol>

                            <p class="mt-4 text-muted">
                                Tip: If you don’t see “just bookmark it!” in the Share Sheet, scroll to the bottom, tap <strong>“Edit Actions”</strong>, and add it to your favorites.
                            </p>
                        </div>
                    </div>

                    <div class="row justify-content-center mt-4">
                        <div class="col-md-6">
                            <video src="@/assets/help/help-ios-app-saving-a-bookmark.mp4"
                                   type="video/mp4"
                                   controls = "true"
                                   autoplay="autoplay"
                                   muted="muted"
                                   loop="loop"
                                   playsinline="playsinline"
                                   preload="auto"
                                   class="img-fluid rounded shadow">
                                Your browser does not support the video tag.
                            </video>
                        </div>
                    </div>
                </div>
            </section>

            <!-- Footer -->
            <footer class="bg-light text-center py-4">
                <div class="container">
                    <p class="mb-0">Still have questions? <a href="mailto:support@justbookmark.it">Contact us</a></p>
                </div>
            </footer>
    <tlfooter/>
</div>
</template>


<script>
import navbar from "@/components/NavBar";
import tlfooter from "@/components/Footer";
import ErrorPanel from "@/components/ErrorPanel";
import Bookmarklet from "@/components/Bookmarklet";

export default {
    name: "Help",
    head: {
        title: 'just bookmark it! - help'
    },
    components: {
        ErrorPanel, navbar, tlfooter, Bookmarklet
    }
};
</script>

<style>
.section {
    padding: 4rem 0;
}

</style>